import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/apiClient';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@pagespeedify/ui';
import { ApiResponse, Task, TaskStatus, TaskPriority } from '../types';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task?: Task | null;
  onSuccess: (result?: { success?: boolean }) => void;
  allTags: string[];
}

// Helper function to format Core Web Vitals with proper units
const formatCoreWebVitalsValue = (key: string, value: any): string => {
  if (value === null || value === undefined) return 'N/A';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  switch (key.toLowerCase()) {
    case 'cls':
      return numValue.toFixed(3);
    case 'fcp':
    case 'lcp':
      return `${(numValue / 1000).toFixed(1)}s`;
    case 'tbt':
    case 'inp':
    case 'ttfb':
      return `${Math.round(numValue)}ms`;
    default:
      return String(value);
  }
};

// Helper function to get relevant Core Web Vitals based on task title
const getRelevantCoreWebVitals = (coreWebVitals: any, taskTitle: string): Record<string, string> => {
  if (!coreWebVitals || typeof coreWebVitals !== 'object') return {};
  
  const title = taskTitle.toLowerCase();
  const relevantMetrics: Record<string, string> = {};
  
  // Check what metrics are relevant for this specific task
  if (title.includes('first contentful paint') || title.includes('fcp')) {
    if (coreWebVitals.fcp !== undefined) relevantMetrics.FCP = formatCoreWebVitalsValue('fcp', coreWebVitals.fcp);
    if (coreWebVitals.lcp !== undefined) relevantMetrics.LCP = formatCoreWebVitalsValue('lcp', coreWebVitals.lcp);
  } else if (title.includes('largest contentful paint') || title.includes('lcp')) {
    if (coreWebVitals.lcp !== undefined) relevantMetrics.LCP = formatCoreWebVitalsValue('lcp', coreWebVitals.lcp);
    if (coreWebVitals.fcp !== undefined) relevantMetrics.FCP = formatCoreWebVitalsValue('fcp', coreWebVitals.fcp);
  } else if (title.includes('cumulative layout shift') || title.includes('cls')) {
    if (coreWebVitals.cls !== undefined) relevantMetrics.CLS = formatCoreWebVitalsValue('cls', coreWebVitals.cls);
  } else if (title.includes('total blocking time') || title.includes('tbt')) {
    if (coreWebVitals.tbt !== undefined) relevantMetrics.TBT = formatCoreWebVitalsValue('tbt', coreWebVitals.tbt);
    if (coreWebVitals.inp !== undefined) relevantMetrics.INP = formatCoreWebVitalsValue('inp', coreWebVitals.inp);
  } else if (title.includes('time to first byte') || title.includes('ttfb')) {
    if (coreWebVitals.ttfb !== undefined) relevantMetrics.TTFB = formatCoreWebVitalsValue('ttfb', coreWebVitals.ttfb);
  } else if (title.includes('speed index')) {
    // Speed Index relates to FCP and LCP
    if (coreWebVitals.fcp !== undefined) relevantMetrics.FCP = formatCoreWebVitalsValue('fcp', coreWebVitals.fcp);
    if (coreWebVitals.lcp !== undefined) relevantMetrics.LCP = formatCoreWebVitalsValue('lcp', coreWebVitals.lcp);
  } else if (title.includes('time to interactive') || title.includes('interactive')) {
    // TTI relates to TBT and FCP
    if (coreWebVitals.tbt !== undefined) relevantMetrics.TBT = formatCoreWebVitalsValue('tbt', coreWebVitals.tbt);
    if (coreWebVitals.fcp !== undefined) relevantMetrics.FCP = formatCoreWebVitalsValue('fcp', coreWebVitals.fcp);
  } else {
    // For general tasks, show the most important metrics
    if (coreWebVitals.fcp !== undefined) relevantMetrics.FCP = formatCoreWebVitalsValue('fcp', coreWebVitals.fcp);
    if (coreWebVitals.lcp !== undefined) relevantMetrics.LCP = formatCoreWebVitalsValue('lcp', coreWebVitals.lcp);
    if (coreWebVitals.cls !== undefined) relevantMetrics.CLS = formatCoreWebVitalsValue('cls', coreWebVitals.cls);
  }
  
  return relevantMetrics;
};

// Helper to process description: render images and HTML safely
// In renderDescriptionHTML, improve image regex to match more data URIs and URLs
function renderDescriptionHTML(description: string) {
  if (!description) return null;
  // Replace data:image URLs with <img> tags
  const imgDataUrlRegex = /(data:image\/(?:png|jpeg|jpg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=]+={0,2})/g;
  let html = description.replace(imgDataUrlRegex, (match) => `<img src='${match}' alt='Embedded image' style='max-width:100%;max-height:200px;display:block;margin:8px 0;' />`);
  // Replace image links (ending with .png, .jpg, .jpeg, .gif, .webp, .svg) with <img> tags
  const imgUrlRegex = /(https?:\/\/[\w\-./%?=&+#:]+\.(?:png|jpe?g|gif|webp|svg))(?![\w])/gi;
  html = html.replace(imgUrlRegex, (url) => `<img src='${url}' alt='Embedded image' style='max-width:100%;max-height:200px;display:block;margin:8px 0;' />`);
  // Replace all other links with <a> tags (open in new tab)
  html = html.replace(/(https?:\/\/[\w\-./%?=&+#:]+)(?![^<]*?>)/gi, (url) => {
    // If already inside an <img src=...> tag, skip
    if (html.includes(`<img src='${url}'`)) return url;
    return `<a href='${url}' target='_blank' rel='noopener noreferrer'>${url}</a>`;
  });
  // Sanitize
  return <div className="prose prose-sm max-w-full overflow-x-auto" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
}

// Helper to prettify image/file titles
function prettifyFileLabel(file: string): string {
  if (!file) return '';
  if (file.startsWith('data:image')) return 'Embedded Image';
  try {
    const url = new URL(file);
    return url.pathname.split('/').pop() || url.hostname;
  } catch {
    // Not a valid URL, fallback to last part after /
    const parts = file.split('/');
    return parts[parts.length - 1] || file.slice(0, 30) + (file.length > 30 ? '...' : '');
  }
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, onClose, task, onSuccess, allTags = [] }) => {
  const apiClient = useApi();
  const queryClient = useQueryClient();
  const isEditMode = !!task;
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('medium');
  const [status, setStatus] = useState<TaskStatus>('open');
  const [tag, setTag] = useState('');
  // Context editing states
  const [contextDescription, setContextDescription] = useState('');
  const [contextFiles, setContextFiles] = useState('');
  const [hasContext, setHasContext] = useState(false);
  // Removed allTagsState and setAllTags, now only use allTags prop

  useEffect(() => {
    if (isOpen) {
      if (task) {
        setTitle(task.title || '');
        setNotes(task.description || task.notes || '');
        setPriority((typeof task.priority === 'string' ? task.priority : 'medium') as TaskPriority);
        setStatus(task.status || 'open');
        setTag(task.tag || '');
        
        // Check if task has context data
        const hasContextData = !!(task.context && 
          (task.context.pageUrl || 
           task.context.device || 
           task.context.testDate || 
           task.context.metric ||
           task.context.insight ||
           task.context.auditData));
        setHasContext(hasContextData);
          // Initialize context editing fields
        setContextDescription(task.context?.contextDescription || '');
        setContextFiles(task.context?.affectedFiles || '');
      } else {
        // Reset form for new task
        setTitle('');
        setNotes('');
        setPriority('medium');
        setStatus('open');
        setTag('');
        setHasContext(false);
        setContextDescription('');
        setContextFiles('');
      }
    }
  }, [task, isOpen]);

  const validateTextContent = (text: string): string | null => {
    if (!text.trim()) return null;
    
    // Check for excessively long content
    if (text.length > 2000) {
      return 'Content is too long (max 2000 characters)';
    }
    
    return null;
  };

  const validateFileList = (files: string): string | null => {
    if (!files.trim()) return null;
    
    const lines = files.split('\n').filter(line => line.trim());
    for (const line of lines) {
      // Basic file path validation
      if (line.length > 500) {
        return 'File path too long';
      }
      // Check for suspicious characters
      if (/[<>"|*?]/.test(line)) {
        return 'File paths contain invalid characters';
      }
    }
    
    return null;
  };

  const mutation = useMutation<ApiResponse<Task>, Error, Partial<Task>>({
    mutationFn: (payload) => {
      const apiCall = isEditMode
        ? apiClient.patch(`/tasks/${task?.id}`, payload)
        : apiClient.post('/tasks', payload);
      return apiCall as Promise<ApiResponse<Task>>;
    },
    onSuccess: () => {
      toast.success(isEditMode ? 'Task updated successfully!' : 'Task created successfully!');
      // Only call onSuccess with { success: true } so parent can refetch only on actual create/edit
      onSuccess({ success: true });
      onClose();
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || error?.message || `Unable to ${isEditMode ? 'update' : 'create'} task. Please try again.`;
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) {
        toast.error('Please enter a task title');
        return;
    }
      // Validate context fields if they're being edited
    if (hasContext || contextDescription || contextFiles) {
      const descValidation = validateTextContent(contextDescription);
      if (descValidation) {
        toast.error(`Additional Notes: ${descValidation}`);
        return;
      }
      
      const filesValidation = validateFileList(contextFiles);
      if (filesValidation) {
        toast.error(`Affected Files: ${filesValidation}`);
        return;
      }
    }

    // Always include a type field in context for manual task creation
    const payload: Partial<Task> = {
      title,
      notes,
      priority,
      type: 'custom', // Always send type for new tasks
      tag,
    };
    if (contextDescription || contextFiles) {
      payload.context = {
        type: 'custom',
        contextDescription: contextDescription || undefined,
        affectedFiles: contextFiles || undefined,
      };
    } else if (hasContext && isEditMode && task?.context) {
      payload.context = {
        ...task.context,
        type: task.context.type || 'custom',
        contextDescription: contextDescription || undefined,
        affectedFiles: contextFiles || undefined,
      };
    }
    
    if (isEditMode) {
        payload.status = status;
        if (task?.type) payload.type = task.type; // For edit, use the existing type
    }
    // Debug: show payload if error persists
    // toast.info(JSON.stringify(payload));
    // In handleSubmit, enforce tag count limit for new tags
    if (!isEditMode && tag && !allTags.includes(tag) && allTags.length >= 20) {
      toast.error('You can only have up to 20 unique tags. Please reuse or remove an existing tag.');
      return;
    }
    mutation.mutate(payload);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Task' : 'Create New Task'}</DialogTitle>
            <DialogDescription>
              {isEditMode ? 'Update the details of this task.' : "Manually create a new task to track. It will be marked as a 'custom' type."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title Field */}
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium">
                Title
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Optimize hero image"
                required
              />
            </div>
            {/* Tag Field */}
            <div className="space-y-2">
              <Label htmlFor="tag" className="text-sm font-medium">
                Tag (optional)
              </Label>
              <Input
                id="tag"
                value={tag}
                onChange={(e) => {
                  let value = e.target.value.slice(0, 10); // Limit tag length to 10
                  setTag(value);
                }}
                placeholder="e.g., my-site"
                maxLength={10}
                list="tag-suggestions"
              />
              <datalist id="tag-suggestions">
                {allTags.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
              <p className="text-xs text-muted-foreground">
                Use a tag to group related tasks (max 10 chars, up to 20 unique tags). <br />
                <span className="italic">Tags are for your organization only—they are not used for AI context.</span>
              </p>
            </div>
            {/* Description Field */}
            {isEditMode && task?.description ? (
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description (from Report)
                </Label>
                <div className="bg-muted/30 p-3 rounded-md border text-sm prose prose-sm max-w-full overflow-x-auto">
                  {renderDescriptionHTML(task.description)}
                </div>
                <p className="text-xs text-muted-foreground">
                  This description was automatically generated from the performance report.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">
                  Description
                </Label>
                <Textarea
                  id="description"
                  value={notes || ''}
                  onChange={(e) => setNotes(e.target.value)}
                  className="min-h-[80px] resize-y"
                  placeholder="(Optional) A brief description of the task. Supports Markdown formatting."
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground">
                  {1000 - (notes?.length || 0)} characters remaining • Markdown supported
                </p>
              </div>
            )}

            {/* Priority and Status Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="priority" className="text-sm font-medium">
                  Priority
                </Label>
                <Select value={String(priority)} onValueChange={(v) => setPriority(v as TaskPriority)}>
                  <SelectTrigger className="w-full bg-background text-foreground border border-input">
                    <SelectValue placeholder="Select priority" className="text-foreground" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover text-popover-foreground border border-border">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {isEditMode && (
                <div className="space-y-2">
                  <Label htmlFor="status" className="text-sm font-medium">
                    Status
                  </Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
                    <SelectTrigger className="w-full bg-background text-foreground border border-input">
                      <SelectValue placeholder="Select status" className="text-foreground" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover text-popover-foreground border border-border">
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Context editing section for tasks with context data */}
            <div className="space-y-4">
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="context">
                  <AccordionTrigger className="text-sm font-medium">
                    Additional Context & Files
                  </AccordionTrigger>
                  <AccordionContent className="space-y-6 pt-4">
                    {/* Read-only context information (if any, only in edit mode) */}
                    {isEditMode && task?.context && (
                      <div className="space-y-4">
                        {/* Basic Context */}
                        <div className="bg-muted/30 p-4 rounded-md border">
                          <h4 className="text-sm font-medium mb-3">Task Context (Read-only)</h4>
                          <div className="text-xs text-muted-foreground space-y-2">
                            {task.context.pageUrl && (
                              <div className="flex flex-wrap gap-1">
                                <strong className="min-w-0">Page:</strong> 
                                <span className="break-all">{task.context.pageUrl}</span>
                              </div>
                            )}
                            {task.context.device && (
                              <div><strong>Device:</strong> {task.context.device}</div>
                            )}
                            {task.context.testDate && (
                              <div><strong>Test Date:</strong> {new Date(task.context.testDate).toLocaleDateString()}</div>
                            )}
                            {task.context.sourceType && (
                              <div><strong>Source:</strong> {task.context.sourceType.replace(/_/g, ' ')}</div>
                            )}
                            {task.context.performanceScore && (
                              <div><strong>Performance Score:</strong> {task.context.performanceScore}%</div>
                            )}
                          </div>
                        </div>
                        {/* --- NEW: Render context with items (e.g., audit/code issues, offscreen-images, generic, etc.) --- */}
                        {Array.isArray(task.context.items) && task.context.items.length > 0 && (
                          <div className="bg-muted/30 p-4 rounded-md border">
                            <h4 className="text-sm font-medium mb-3">Context Items</h4>
                            <ul className="list-disc ml-6">
                              {task.context.items.map((item: any, idx: number) => (
                                <li key={idx} className="mb-2">
                                  {item.file && (item.file.startsWith('data:image') || item.file.match(/\.(png|jpe?g|gif|webp|svg)$/i)) ? (
                                    <img src={item.file} alt={prettifyFileLabel(item.file)} style={{maxWidth:'100%',maxHeight:200,display:'block',margin:'8px 0'}} />
                                  ) : item.file ? (
                                    <span>{prettifyFileLabel(item.file)}</span>
                                  ) : null}
                                  {item.formattedWasted && (
                                    <span className="ml-2 text-xs text-muted-foreground">({item.formattedWasted})</span>
                                  )}
                                  {item.details && (
                                    <div className="text-xs text-muted-foreground">{item.details}</div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {/* Metric Information */}
                        {task.context.metric && (
                          <div className="bg-muted/30 p-4 rounded-md border">
                            <h4 className="text-sm font-medium mb-3">Metric Details</h4>
                            <div className="text-xs text-muted-foreground space-y-2">
                              <div><strong>Metric:</strong> {task.context.metric.title}</div>
                              <div><strong>Score:</strong> {task.context.metric.score}%</div>
                              {task.context.metric.name && (
                                <div><strong>Metric Name:</strong> {task.context.metric.name}</div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Insight Information */}
                        {task.context.insight && (
                          <div className="bg-muted/30 p-4 rounded-md border">
                            <h4 className="text-sm font-medium mb-3">Insight Details</h4>
                            <div className="text-xs text-muted-foreground space-y-2">
                              {task.context.insight.weight && (
                                <div><strong>Impact Weight:</strong> {task.context.insight.weight}</div>
                              )}
                              {task.context.insight.overallSavingsMs && (
                                <div><strong>Potential Savings:</strong> {task.context.insight.overallSavingsMs}ms</div>
                              )}
                              {task.context.insight.savings && (
                                <div><strong>Savings:</strong> {task.context.insight.savings}</div>
                              )}
                              {task.context.insight.details && (
                                <div><strong>Details:</strong> {task.context.insight.details}</div>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Core Web Vitals */}
                        {task.context.coreWebVitals && (
                          <div className="bg-muted/30 p-4 rounded-md border">
                            <h4 className="text-sm font-medium mb-3">Core Web Vitals</h4>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                              {(() => {
                                const relevantMetrics = getRelevantCoreWebVitals(task.context.coreWebVitals, task.title || '');
                                return Object.entries(relevantMetrics).map(([key, value]) => (
                                  <div key={key} className="text-muted-foreground">
                                    <strong>{key}:</strong> {value}
                                  </div>
                                ));
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}                      {/* Show affected resources if available */}
                    {task?.context?.affectedResources && task.context.affectedResources.length > 0 && (
                      <div className="bg-muted/30 p-4 rounded-md border">
                        <h4 className="text-sm font-medium mb-3">Affected Resources ({task.context.affectedResources.length})</h4>
                        <div className="max-h-40 overflow-y-auto bg-background/50 p-2 rounded">
                          {task.context.affectedResources.slice(0, 15).map((resource: any, index: number) => (
                            <div key={index} className="text-xs text-muted-foreground py-1 break-all">
                              {resource.url || resource.file || resource}
                            </div>
                          ))}
                          {task.context.affectedResources.length > 15 && (
                            <div className="text-xs text-muted-foreground italic mt-2">
                              ... and {task.context.affectedResources.length - 15} more resources
                            </div>
                          )}
                        </div>
                      </div>
                    )}{/* Show audit data/code issues if available */}
                    {task?.context?.auditData && (
                      <div className="bg-muted/30 p-4 rounded-md border">
                        <h4 className="text-sm font-medium mb-3">Code Issues & Audit Information (Read-only)</h4>
                        <div className="text-xs text-muted-foreground space-y-3">
                          {task.context.auditData.title && (
                            <div><strong>Audit:</strong> {task.context.auditData.title}</div>
                          )}
                          {task.context.auditData.description && (
                            <div>
                              <strong>Description:</strong> 
                              <div className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap bg-background/50 p-2 rounded text-xs">
                                {task.context.auditData.description}
                              </div>
                            </div>
                          )}
                          {task.context.auditData.details?.items && task.context.auditData.details.items.length > 0 && (
                            <div>
                              <strong>Issues Found:</strong> {task.context.auditData.details.items.length} items
                              <div className="mt-2 max-h-40 overflow-y-auto bg-background/50 p-2 rounded">
                                {task.context.auditData.details.items.slice(0, 5).map((item: any, index: number) => (
                                  <div key={index} className="mb-2 last:mb-0 text-xs break-all">
                                    {typeof item === 'string' ? item : (
                                      <>
                                        {item.url && <div><strong>URL:</strong> {item.url}</div>}
                                        {item.node && item.node.snippet && (
                                          <div><strong>Element:</strong> <code className="bg-muted px-1 rounded">{item.node.snippet}</code></div>
                                        )}                                          {item.wastedBytes && (
                                          <div><strong>Wasted Size:</strong> {
                                            item.wastedBytes >= 1024 
                                              ? `${(item.wastedBytes / 1024).toFixed(1)}KB`
                                              : `${item.wastedBytes} bytes`
                                          }</div>
                                        )}
                                        {item.wastedMs && (
                                          <div><strong>Wasted Time:</strong> {
                                            item.wastedMs >= 1000
                                              ? `${(item.wastedMs / 1000).toFixed(1)}s`
                                              : `${item.wastedMs}ms`
                                          }</div>
                                        )}
                                        {item.reason && <div><strong>Reason:</strong> {item.reason}</div>}
                                      </>
                                    )}
                                  </div>
                                ))}
                                {task.context.auditData.details.items.length > 5 && (
                                  <div className="text-xs text-muted-foreground italic">
                                    ... and {task.context.auditData.details.items.length - 5} more issues
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}                      
                    {/* Editable context fields for all tasks */}
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label htmlFor="contextDescription" className="text-sm font-medium">
                          Your Additional Notes
                        </Label>
                        <Textarea
                          id="contextDescription"
                          value={contextDescription}
                          onChange={(e) => setContextDescription(e.target.value)}
                          className="min-h-[100px] resize-y"
                          placeholder="Add your own notes, implementation details, or additional context about this task (supports Markdown)"
                          maxLength={2000}
                        />
                        <p className="text-xs text-muted-foreground">
                          {2000 - contextDescription.length} characters remaining • Markdown supported
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contextFiles" className="text-sm font-medium">
                          Affected Files
                        </Label>
                        <Textarea
                          id="contextFiles"
                          value={contextFiles}
                          onChange={(e) => setContextFiles(e.target.value)}
                          className="min-h-[120px] resize-y font-mono text-sm"
                          placeholder="List affected files, one per line:&#10;src/components/Hero.tsx&#10;public/images/hero-bg.jpg&#10;styles/main.css"
                        />
                        <p className="text-xs text-muted-foreground">
                          Enter file paths, one per line. Useful for tracking which files need changes.
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Task')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TaskModal;
